/**
 * Shared confirm-upload contract (all dashboards).
 *
 * An upload is complete only when `confirm-upload` returns 200; the presigned
 * storage PUT succeeding is NOT enough. Confirming is idempotent, so a
 * repeated call for the same document is safe.
 *
 *   502 storage outcome unknown   ┐ uncertain: retry confirmation,
 *   503 database outcome unknown  ├ then refetch the document list
 *   504 storage timed out         ┘
 *   403 / 404 / 410 / 415 / 422   terminal: never retried automatically
 */
import { ApiError, isApiError } from './error';

/**
 * 502/503/504: the backend could not confirm the outcome (confirm-upload,
 * document delete). Not a success and not final; the call is safe to repeat.
 */
export function isRetryableStatus(status: number): boolean {
  return status === 502 || status === 503 || status === 504;
}

/** User-facing message for a failed confirm-upload, by status. */
function confirmFailureMessage(err: ApiError): string {
  switch (err.status) {
    case 403:
      return "You don't have access to upload to this request.";
    case 404:
      return 'This request or document could not be found. Refresh the page and try again.';
    case 410:
      return 'The uploaded file was not received. Please upload it again.';
    case 415:
      return "This file's format isn't valid. Please upload a valid file.";
    case 422:
      // Usually a password-protected PDF. Prefer the backend's own reason; if
      // it sent none, err.message is only axios's generic status text.
      return err.payload?.detail || err.payload?.message
        ? err.message
        : "This file can't be used. Password-protected PDFs aren't supported.";
    case 502:
    case 503:
    case 504:
      return "We couldn't confirm your upload. Please try again.";
    default:
      return err.message;
  }
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Run a confirm-upload call. On 502/503/504 it retries a few times with a
 * short backoff (confirmation is idempotent); every other failure is thrown at
 * once. Errors are re-thrown as `ApiError` with a user-facing message and the
 * original status, so callers can keep using `err.message` / `err.status`.
 */
export async function confirmUploadWithRetry<T>(
  confirm: () => Promise<T>,
  { attempts = 3, baseDelayMs = 1000 }: { attempts?: number; baseDelayMs?: number } = {},
): Promise<T> {
  for (let attempt = 1; ; attempt++) {
    try {
      return await confirm();
    } catch (err) {
      if (!isApiError(err)) throw err;
      if (isRetryableStatus(err.status) && attempt < attempts) {
        await sleep(baseDelayMs * attempt);
        continue;
      }
      throw new ApiError(confirmFailureMessage(err), err.status, err.code, err.payload);
    }
  }
}
